import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { 
  Users, Shield, CreditCard, Ticket, Mail, Phone, MessageSquare,
  Settings, BarChart3, Clock, CheckCircle, AlertTriangle, RefreshCw,
  Send, FileText, Search, Filter, Edit, History, Eye, Lock,
  Bell, Zap, HelpCircle, ChevronDown, ChevronRight, ExternalLink,
  Home, Calendar, UserCheck, ShieldCheck, Smartphone, Globe, Star
} from 'lucide-react';

const PlatformDocumentation = () => {
  const [activeSection, setActiveSection] = useState('users');
  const [expandedFeature, setExpandedFeature] = useState(null);

  const toggleFeature = (featureId) => {
    setExpandedFeature(expandedFeature === featureId ? null : featureId);
  };

  // User features documentation
  const userFeatures = [
    {
      id: 'user-registration',
      title: 'Inscription et connexion',
      icon: UserCheck,
      color: 'brand',
      description: 'Créez votre compte et accédez à la plateforme en toute sécurité.',
      steps: [
        'Cliquez sur "Créer un compte" sur la page d\'accueil',
        'Remplissez votre nom, email et mot de passe',
        'Confirmez votre mot de passe',
        'Cliquez sur "S\'inscrire" pour créer votre compte',
        'Vous pouvez maintenant vous connecter avec vos identifiants'
      ],
      tips: [
        'Utilisez un mot de passe fort avec au moins 8 caractères',
        'Votre email sera utilisé pour toutes les communications importantes',
        'Activez l\'authentification à deux facteurs pour plus de sécurité'
      ],
      screenshot: '/docs/auth-page.png'
    },
    {
      id: 'create-transaction',
      title: 'Créer une transaction',
      icon: Ticket,
      color: 'green',
      description: 'Initiez un transfert de billets sécurisé entre acheteur et vendeur.',
      steps: [
        'Connectez-vous à votre compte',
        'Cliquez sur le bouton "Transfert" (bouton flottant à droite)',
        'Sélectionnez si vous êtes acheteur ou vendeur',
        'Remplissez les informations de l\'événement (nom, date, heure)',
        'Sélectionnez la plateforme où se trouvent les billets (Ticketmaster, Evenko, etc.)',
        'Consultez le guide de transfert pour votre plateforme',
        'Entrez les informations des billets (section, rangée, sièges)',
        'Indiquez le prix convenu et le nombre de billets',
        'Entrez les coordonnées de l\'autre partie (email, téléphone)',
        'Choisissez votre préférence de notification (email, SMS ou les deux)',
        'Vérifiez le récapitulatif et confirmez la création'
      ],
      tips: [
        'Vérifiez bien toutes les informations avant de confirmer',
        'Les détails ne peuvent pas être modifiés après confirmation',
        'Les deux parties recevront un email de confirmation',
        'Frais: 6,95$ (≤125$), 9,95$ (125-250$) ou 19,95$ (>250$) selon le montant total du transfert.'
      ],
      screenshot: '/docs/create-transaction.png'
    },
    {
      id: 'confirm-transaction',
      title: 'Confirmer une transaction',
      icon: CheckCircle,
      color: 'blue',
      description: 'Confirmez votre participation à une transaction via email ou votre compte.',
      steps: [
        'Ouvrez l\'email de confirmation reçu de Middleman',
        'Vérifiez le récapitulatif de la transaction',
        'Cliquez sur le bouton "Confirmer la transaction"',
        'Vous serez redirigé vers une page de confirmation',
        'La confirmation est enregistrée automatiquement'
      ],
      tips: [
        'Vous avez 4 heures pour confirmer',
        'Un rappel est envoyé après 12 heures',
        'La transaction expire automatiquement après 4h sans confirmation',
        'Contactez le support si vous avez besoin de plus de temps'
      ],
      screenshot: '/docs/confirm-transaction.png'
    },
    {
      id: 'buyer-steps',
      title: 'Étapes de l\'acheteur',
      icon: CreditCard,
      color: 'brand',
      description: 'Processus complet pour l\'acheteur, de la confirmation au paiement.',
      steps: [
        '<strong>Étape 1: Confirmation</strong> - Confirmez les informations de la demande de transfert sécurisé',
        '<strong>Étape 2: Paiement</strong> - Payez le montant convenu + frais Middleman (6,95$ à 19,95$ selon le montant total). Les fonds sont mis en séquestre.',
        '<strong>Étape 3: Réception</strong> - Middleman vous envoie les billets après vérification'
      ],
      tips: [
        'Vos fonds sont protégés en séquestre pendant tout le processus',
        'Middleman vérifie l\'authenticité des billets avant de vous les envoyer',
        'Vous recevrez une notification à chaque étape complétée'
      ],
      screenshot: '/docs/buyer-steps.png'
    },
    {
      id: 'seller-steps',
      title: 'Étapes du vendeur',
      icon: Send,
      color: 'purple',
      description: 'Processus complet pour le vendeur, de la confirmation à la réception du paiement.',
      steps: [
        '<strong>Étape 1: Confirmation</strong> - Confirmez les détails de la demande de transfert sécurisé',
        '<strong>Étape 2: Envoi des billets</strong> - Transférez vos billets à info@transfertdebillets.ca. Middleman confirme la réception et vérifie via la plateforme officielle.',
        '<strong>Étape 3: Paiement</strong> - Middleman vous envoie le paiement après réception des fonds de l\'acheteur'
      ],
      tips: [
        'Suivez le guide de transfert spécifique à votre plateforme',
        'Conservez une preuve de transfert (capture d\'écran)',
        'Le paiement est envoyé dès que l\'acheteur a payé et les billets sont vérifiés'
      ],
      screenshot: '/docs/seller-steps.png'
    },
    {
      id: 'transfer-guide',
      title: 'Guide de transfert par plateforme',
      icon: HelpCircle,
      color: 'amber',
      description: 'Instructions détaillées pour transférer vos billets depuis chaque plateforme.',
      steps: [
        'Lors de la création de transaction, sélectionnez votre plateforme',
        'Cliquez sur "Comment transférer vos billets" pour ouvrir le guide',
        'Suivez les étapes numérotées spécifiques à votre plateforme',
        'Cochez chaque étape complétée pour suivre votre progression',
        'Regardez la vidéo tutoriel si disponible (Ticketmaster, AXS)',
        'Consultez les conseils et le lien vers le support officiel'
      ],
      tips: [
        '14 plateformes sont supportées avec des instructions détaillées',
        'Les vidéos tutoriels facilitent le processus',
        'Si votre plateforme n\'est pas listée, choisissez "Autre"'
      ],
      screenshot: '/docs/transfer-guide.png'
    },
    {
      id: 'notifications',
      title: 'Notifications et alertes',
      icon: Bell,
      color: 'rose',
      description: 'Restez informé à chaque étape via email, SMS ou les deux.',
      steps: [
        'Choisissez votre préférence lors de la création de transaction',
        '<strong>Email</strong>: Notifications détaillées avec récapitulatif',
        '<strong>SMS</strong>: Messages concis avec prochaine action',
        '<strong>Les deux</strong>: Email + SMS pour ne rien manquer',
        'Recevez des rappels automatiques après 12 heures',
        'Notification de progression à chaque étape complétée'
      ],
      tips: [
        'Vérifiez vos spams si vous ne recevez pas les emails',
        'Assurez-vous que votre numéro de téléphone est correct',
        'Les notifications sont envoyées en temps réel'
      ],
      screenshot: '/docs/notifications.png'
    },
    {
      id: 'support-contact',
      title: 'Contacter le support',
      icon: MessageSquare,
      color: 'indigo',
      description: 'Obtenez de l\'aide via le chatbot FAQ ou le formulaire de contact.',
      steps: [
        'Cliquez sur le bouton "Support" (bouton flottant à droite)',
        'Posez votre question au chatbot pour une réponse immédiate',
        'Si le chatbot ne peut pas aider, un ticket de support est créé',
        'Vous pouvez aussi aller sur la page Contact pour soumettre un formulaire',
        'L\'équipe répond selon la priorité (maintenant, 24h, 48h, 72h)'
      ],
      tips: [
        'Le chatbot répond aux questions fréquentes instantanément',
        'Les tickets urgents sont traités en priorité',
        'Vous recevrez une réponse par email'
      ],
      screenshot: '/docs/support.png'
    }
  ];

  // Admin features documentation
  const adminFeatures = [
    {
      id: 'admin-dashboard',
      title: 'Tableau de bord administrateur',
      icon: BarChart3,
      color: 'brand',
      description: 'Vue d\'ensemble de toutes les activités de la plateforme.',
      steps: [
        'Accédez au Super Admin Dashboard via le menu',
        'Consultez les statistiques clés en haut de page',
        'Naviguez entre les onglets: Vue d\'ensemble, Utilisateurs, Transactions, Statistiques, Facebook, Support, Paramètres'
      ],
      tips: [
        'Le tableau de bord se met à jour automatiquement',
        'Utilisez les filtres pour affiner les données',
        'Exportez les données si nécessaire'
      ],
      screenshot: '/docs/admin-dashboard.png'
    },
    {
      id: 'transaction-management',
      title: 'Gestion des transactions',
      icon: Ticket,
      color: 'green',
      description: 'Gérez, modifiez et réactivez les transactions depuis l\'interface admin.',
      steps: [
        'Allez dans l\'onglet "Transactions"',
        'Utilisez la recherche pour trouver une transaction (par événement, email, ID)',
        'Filtrez par statut (en attente, confirmé, expiré, etc.)',
        '<strong>Voir les étapes</strong>: Consultez la progression acheteur/vendeur',
        '<strong>Historique</strong>: Voir toutes les actions admin sur cette transaction',
        '<strong>Modifier</strong>: Changez les emails, téléphones, prix, etc.',
        '<strong>Réactiver</strong>: Réactivez une transaction expirée (nouveaux emails envoyés)',
        '<strong>Expirer</strong>: Expirer manuellement une transaction'
      ],
      tips: [
        'La réactivation envoie de nouveaux emails de confirmation',
        'Toutes les modifications sont enregistrées dans l\'historique',
        'Les transactions réactivées ont un nouveau délai de 4h par défaut'
      ],
      screenshot: '/docs/transaction-management.png'
    },
    {
      id: 'analytics-dashboard',
      title: 'Statistiques et analyses',
      icon: BarChart3,
      color: 'blue',
      description: 'Analysez les performances avec des métriques détaillées.',
      steps: [
        'Allez dans l\'onglet "Statistiques"',
        '<strong>Résumé</strong>: Total, complétées, en cours, expirées',
        '<strong>Entonnoir de conversion</strong>: Visualisez la progression des transactions',
        '<strong>Taux de confirmation</strong>: Acheteur vs vendeur',
        '<strong>Délais moyens</strong>: Temps par étape',
        '<strong>Tendance hebdomadaire</strong>: Évolution sur 8 semaines',
        '<strong>Analyse des expirations</strong>: Pourquoi les transactions expirent',
        '<strong>Efficacité des rappels</strong>: Impact des rappels automatiques'
      ],
      tips: [
        'Les statistiques aident à identifier les problèmes de conversion',
        'Surveillez le taux d\'expiration (alerte si >15%)',
        'Utilisez les données pour améliorer le processus'
      ],
      screenshot: '/docs/analytics.png'
    },
    {
      id: 'user-management',
      title: 'Gestion des utilisateurs',
      icon: Users,
      color: 'purple',
      description: 'Gérez les comptes utilisateurs et leurs permissions.',
      steps: [
        'Allez dans l\'onglet "Utilisateurs"',
        'Recherchez un utilisateur par nom ou email',
        'Consultez les détails du compte',
        'Modifiez le rôle (user, admin, super_admin)',
        'Réinitialisez le mot de passe si nécessaire',
        'Désactivez ou supprimez un compte'
      ],
      tips: [
        'Les super admins ont accès à toutes les fonctionnalités',
        'Les admins peuvent gérer les transactions et le support',
        'Soyez prudent avec les suppressions de compte'
      ],
      screenshot: '/docs/user-management.png'
    },
    {
      id: 'support-tickets',
      title: 'Gestion des tickets de support',
      icon: MessageSquare,
      color: 'amber',
      description: 'Traitez les demandes de support des utilisateurs.',
      steps: [
        'Allez dans l\'onglet "Support"',
        'Consultez les statistiques (à traiter, en cours, résolus)',
        'Les tickets sont triés par priorité (maintenant, 24h, 48h, 72h)',
        'Cliquez sur un ticket pour voir les détails',
        '<strong>Notes internes</strong>: Ajoutez des notes visibles uniquement par les admins',
        '<strong>Réponses</strong>: Envoyez une réponse au client',
        'Changez le statut et la priorité au besoin',
        'Le compte à rebours indique le temps restant avant la deadline'
      ],
      tips: [
        'Répondez aux tickets urgents en premier',
        'Utilisez les notes internes pour communiquer avec l\'équipe',
        'Les réponses sont envoyées par email au client'
      ],
      screenshot: '/docs/support-tickets.png'
    },
    {
      id: 'expiration-alerts',
      title: 'Alertes de taux d\'expiration',
      icon: AlertTriangle,
      color: 'red',
      description: 'Recevez des alertes automatiques si le taux d\'expiration est trop élevé.',
      steps: [
        'Le système vérifie quotidiennement le taux d\'expiration',
        'Si le taux dépasse 15%, une alerte est envoyée à l\'admin',
        'L\'email contient les statistiques détaillées',
        'Actions recommandées pour améliorer le taux',
        'Lien direct vers le tableau de bord analytique'
      ],
      tips: [
        'Un taux d\'expiration élevé peut indiquer des problèmes',
        'Vérifiez que les emails de confirmation arrivent bien',
        'Analysez les transactions expirées pour identifier des patterns'
      ],
      screenshot: '/docs/expiration-alerts.png'
    },
    {
      id: 'platform-settings',
      title: 'Paramètres de la plateforme',
      icon: Settings,
      color: 'gray',
      description: 'Configurez les paramètres globaux de la plateforme.',
      steps: [
        'Allez dans l\'onglet "Paramètres"',
        'Modifiez les sections visibles sur la page d\'accueil',
        'Configurez les frais de service',
        'Gérez les paramètres de notification',
        'Configurez l\'authentification à deux facteurs',
        'Testez les intégrations (Stripe, Twilio, Resend)'
      ],
      tips: [
        'Testez les changements avant de les appliquer en production',
        'Les paramètres affectent tous les utilisateurs',
        'Documentez les changements importants'
      ],
      screenshot: '/docs/settings.png'
    },
    {
      id: 'facebook-groups',
      title: 'Gestion des groupes Facebook',
      icon: Globe,
      color: 'blue',
      description: 'Modérez les soumissions de groupes Facebook de la communauté.',
      steps: [
        'Allez dans l\'onglet "Facebook"',
        'Consultez les groupes en attente de validation',
        'Vérifiez les informations soumises',
        'Approuvez ou rejetez chaque soumission',
        'Les groupes approuvés apparaissent dans la section Communautés'
      ],
      tips: [
        'Vérifiez que les groupes sont légitimes',
        'Les groupes de fraude doivent être rejetés',
        'Contactez le soumissionnaire si besoin de clarification'
      ],
      screenshot: '/docs/facebook-groups.png'
    },
    {
      id: 'popup-management',
      title: 'Gestion des popups',
      icon: Bell,
      color: 'purple',
      description: 'Créez des notifications popup personnalisées pour vos utilisateurs.',
      steps: [
        'Allez dans l\'onglet "Popups"',
        'Cliquez sur "Nouveau popup" pour créer une notification',
        'Remplissez le titre et le contenu du message',
        'Choisissez le type: Information, Avertissement, Succès ou Promotion',
        'Sélectionnez l\'audience cible: Clients, Employés ou Tout le monde',
        '<strong>Contenu enrichi</strong>: Ajoutez une image (URL), des boutons d\'action avec liens',
        '<strong>Programmation</strong>: Définissez des dates de début et fin',
        '<strong>Assignation</strong>: Ciblez des utilisateurs spécifiques si besoin',
        'Activez "Une seule fois" pour n\'afficher qu\'une fois par utilisateur',
        'Consultez l\'onglet "Statistiques" pour voir les vues et clics'
      ],
      tips: [
        'Utilisez l\'aperçu en temps réel pour voir le rendu',
        'Les popups s\'affichent après la connexion de l\'utilisateur',
        'Le CTR (taux de clic) aide à mesurer l\'efficacité',
        'Désactivez un popup sans le supprimer pour le réutiliser plus tard'
      ],
      screenshot: '/docs/popup-management.png'
    },
    {
      id: 'ratings-management',
      title: 'Gestion des évaluations',
      icon: Star,
      color: 'amber',
      description: 'Consultez et analysez les évaluations laissées par les utilisateurs.',
      steps: [
        'Allez dans l\'onglet "Avis"',
        '<strong>Aperçu</strong>: Consultez la note moyenne, le total des évaluations et la distribution',
        '<strong>Par utilisateur</strong>: Voir les notes reçues par chaque utilisateur',
        '<strong>Récentes</strong>: Historique des dernières évaluations avec filtres',
        'Cliquez sur une évaluation pour voir les détails complets',
        'Filtrez par évaluations positives (4-5★) ou négatives (1-2★)'
      ],
      tips: [
        'Les évaluations négatives (1-2★) déclenchent une alerte email automatique',
        'Surveillez les utilisateurs avec des notes basses récurrentes',
        'Les statistiques de satisfaction détaillées aident à identifier les problèmes',
        'Le système inclut des questions sur: communication, rapidité, qualité'
      ],
      screenshot: '/docs/ratings-management.png'
    },
    {
      id: 'sms-notifications',
      title: 'Gestion des notifications SMS',
      icon: Smartphone,
      color: 'brand',
      description: 'Configurez quelles notifications SMS sont envoyées et à qui.',
      steps: [
        'Allez dans l\'onglet "SMS"',
        'Vérifiez que le badge "Twilio actif" est affiché',
        '<strong>Activer/Désactiver</strong>: Utilisez les toggles pour chaque type de notification',
        '<strong>Destinataires</strong>: Choisissez qui reçoit chaque notification (Acheteur, Vendeur, Admin)',
        'Cliquez sur "Sauvegarder" après vos modifications',
        'Utilisez "Tout activer" ou "Tout désactiver" pour un contrôle rapide'
      ],
      tips: [
        'Les notifications sont groupées par catégorie: Cycle de transaction, Rappels, Litiges',
        'Le SMS "Souhaits avant l\'événement" est envoyé 24h avant l\'événement à l\'acheteur',
        'Les alertes admin sont envoyées pour les évaluations négatives et litiges',
        'Désactivez les notifications non essentielles pour réduire les coûts SMS'
      ],
      screenshot: '/docs/sms-notifications.png'
    },
    {
      id: 'admin-permissions',
      title: 'Gestion des permissions',
      icon: ShieldCheck,
      color: 'indigo',
      description: 'Contrôlez les accès de chaque administrateur aux différentes fonctionnalités.',
      steps: [
        'Allez dans l\'onglet "Perms" (Permissions)',
        'Sélectionnez un administrateur dans la liste',
        'Activez ou désactivez chaque permission individuellement:',
        '• <strong>Utilisateurs</strong>: Gestion des comptes',
        '• <strong>Transactions</strong>: Gestion des transactions',
        '• <strong>Statistiques</strong>: Accès aux analytics',
        '• <strong>Facebook</strong>: Modération des groupes',
        '• <strong>Support</strong>: Gestion des tickets',
        '• <strong>Documentation</strong>: Accès au guide',
        '• <strong>Paramètres</strong>: Configuration de la plateforme',
        'Cliquez sur "Sauvegarder" pour appliquer les changements'
      ],
      tips: [
        'Les Super Admins ont toujours accès à toutes les fonctionnalités',
        'Limitez les permissions des admins réguliers selon leurs responsabilités',
        'Utilisez "Tout activer" ou "Tout désactiver" pour un contrôle rapide',
        'Les changements prennent effet immédiatement'
      ],
      screenshot: '/docs/admin-permissions.png'
    },
    {
      id: 'platform-guides',
      title: 'Guides de transfert par plateforme',
      icon: Ticket,
      color: 'brand',
      description: 'Consultez les guides détaillés pour transférer des billets depuis chaque plateforme supportée.',
      steps: [
        'Les guides sont disponibles sur la page publique <a href="/platform-guides" target="_blank" class="text-brand-600 underline hover:text-brand-800">/platform-guides</a>',
        'Chaque plateforme (Ticketmaster, AXS, DICE, etc.) a son propre guide étape par étape',
        'Les guides incluent les URL de connexion directe vers chaque plateforme',
        'Les instructions sont adaptées aux spécificités de chaque service de billetterie',
        'Utilisez l\'onglet "Billets" dans le menu admin pour gérer les plateformes'
      ],
      tips: [
        'Partagez le lien /platform-guides aux vendeurs qui ont besoin d\'aide',
        'Les plateformes peuvent être ajoutées, modifiées ou masquées depuis l\'admin',
        'L\'ordre des plateformes dans la liste peut être personnalisé',
        'Les changements aux plateformes se reflètent immédiatement dans le formulaire de transfert'
      ],
      screenshot: '/docs/platform-guides.png'
    }
  ];

  const renderFeatureCard = (feature, index) => {
    const Icon = feature.icon;
    const isExpanded = expandedFeature === feature.id;
    
    const colorClasses = {
      brand: 'bg-brand-100 text-brand-600 border-brand-200',
      green: 'bg-green-100 text-green-600 border-green-200',
      blue: 'bg-blue-100 text-blue-600 border-blue-200',
      purple: 'bg-purple-100 text-purple-600 border-purple-200',
      amber: 'bg-amber-100 text-amber-600 border-amber-200',
      rose: 'bg-rose-100 text-rose-600 border-rose-200',
      indigo: 'bg-indigo-100 text-indigo-600 border-indigo-200',
      red: 'bg-red-100 text-red-600 border-red-200',
      gray: 'bg-gray-100 text-gray-600 border-gray-200'
    };

    return (
      <Card 
        key={feature.id} 
        className={`cursor-pointer transition-all hover:shadow-md ${isExpanded ? 'ring-2 ring-brand-500' : ''}`}
        onClick={() => toggleFeature(feature.id)}
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[feature.color]}`}>
                <Icon className="h-6 w-6" />
              </div>
              <div>
                <CardTitle className="text-lg">{feature.title}</CardTitle>
                <CardDescription className="text-sm">{feature.description}</CardDescription>
              </div>
            </div>
            {isExpanded ? (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronRight className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </CardHeader>
        
        {isExpanded && (
          <CardContent className="pt-0" onClick={(e) => e.stopPropagation()}>
            <div className="border-t pt-4 mt-2">
              {/* Screenshot or Visual representation with icon */}
              {feature.screenshot ? (
                <div className="rounded-lg mb-4 overflow-hidden border border-gray-200 shadow-sm">
                  <img 
                    src={feature.screenshot} 
                    alt={feature.title}
                    className="w-full h-auto object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className={`rounded-lg p-6 items-center justify-center ${colorClasses[feature.color]} bg-opacity-20`} style={{display: 'none'}}>
                    <div className="text-center">
                      <Icon className="h-16 w-16 mx-auto mb-2 opacity-80" />
                      <p className="font-medium text-sm">{feature.title}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`rounded-lg p-6 mb-4 flex items-center justify-center ${colorClasses[feature.color]} bg-opacity-20`}>
                  <div className="text-center">
                    <Icon className="h-16 w-16 mx-auto mb-2 opacity-80" />
                    <p className="font-medium text-sm">{feature.title}</p>
                  </div>
                </div>
              )}
              
              {/* Steps */}
              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-brand-600" />
                  Comment utiliser
                </h4>
                <ol className="space-y-2 ml-6">
                  {feature.steps.map((step, idx) => (
                    <li key={idx} className="text-sm text-gray-700 list-decimal" dangerouslySetInnerHTML={{ __html: step }} />
                  ))}
                </ol>
              </div>
              
              {/* Tips */}
              {feature.tips && feature.tips.length > 0 && (
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    Conseils
                  </h4>
                  <ul className="space-y-1">
                    {feature.tips.map((tip, idx) => (
                      <li key={idx} className="text-sm text-amber-700 flex items-start gap-2">
                        <span className="text-amber-500 mt-1">•</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Guide de la plateforme Middleman</h2>
        <p className="text-gray-500">Documentation complète de toutes les fonctionnalités</p>
      </div>

      {/* Section Toggle */}
      <div className="flex justify-center gap-2 mb-6">
        <Button
          variant={activeSection === 'users' ? 'default' : 'outline'}
          onClick={() => setActiveSection('users')}
          className={`text-xs md:text-sm px-3 md:px-4 ${activeSection === 'users' ? 'bg-brand-600 hover:bg-brand-700' : ''}`}
          size="sm"
        >
          <Users className="h-4 w-4 mr-1 md:mr-2" />
          <span className="hidden sm:inline">Section </span>Utilisateurs
        </Button>
        <Button
          variant={activeSection === 'admin' ? 'default' : 'outline'}
          onClick={() => setActiveSection('admin')}
          className={`text-xs md:text-sm px-3 md:px-4 ${activeSection === 'admin' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
          size="sm"
        >
          <Shield className="h-4 w-4 mr-1 md:mr-2" />
          <span className="hidden sm:inline">Section </span>Admin
        </Button>
      </div>

      {/* Section Description */}
      <Card className={`mb-6 ${activeSection === 'users' ? 'bg-brand-50 border-brand-200' : 'bg-purple-50 border-purple-200'}`}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${activeSection === 'users' ? 'bg-brand-100' : 'bg-purple-100'}`}>
              {activeSection === 'users' ? (
                <Users className="h-8 w-8 text-brand-600" />
              ) : (
                <Shield className="h-8 w-8 text-purple-600" />
              )}
            </div>
            <div>
              <h3 className={`text-xl font-bold ${activeSection === 'users' ? 'text-brand-800' : 'text-purple-800'}`}>
                {activeSection === 'users' ? 'Fonctionnalités Utilisateurs' : 'Fonctionnalités Administrateurs'}
              </h3>
              <p className={`${activeSection === 'users' ? 'text-brand-600' : 'text-purple-600'}`}>
                {activeSection === 'users' 
                  ? 'Tout ce que les utilisateurs (acheteurs et vendeurs) peuvent faire sur la plateforme.'
                  : 'Outils de gestion et d\'administration réservés aux employés Middleman.'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {activeSection === 'users' 
          ? userFeatures.map((feature, index) => renderFeatureCard(feature, index))
          : adminFeatures.map((feature, index) => renderFeatureCard(feature, index))
        }
      </div>

      {/* Quick Reference */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-brand-600" />
            Référence rapide
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Contact support</h4>
              <p className="text-sm text-gray-600">support@transfertdebillets.ca</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Envoi des billets</h4>
              <p className="text-sm text-gray-600">info@transfertdebillets.ca</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Frais de service</h4>
              <p className="text-sm text-gray-600">6,95$ (≤125$), 9,95$ (125-250$) ou 19,95$ (>250$)</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Délai de confirmation</h4>
              <p className="text-sm text-gray-600">4 heures maximum</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Rappel automatique</h4>
              <p className="text-sm text-gray-600">Après 12 heures</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Alerte expiration</h4>
              <p className="text-sm text-gray-600">Si taux &gt; 15%</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatformDocumentation;
